import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, IconButton } from '@c2fo/liquidity';
import { EllipsisVerticalIcon } from '@c2fo/liquidity/icons';
import { isTakerMarketOfferable } from '@/enterprise/components/OfferBuilder/utils';
import {
  buildParticipationOffEventPayload,
  buildTurnOffParticipationParams,
  getParticipationOffEventType,
} from '@/enterprise/data/participationOffTracking';
import {
  GlobalOfferMarketPricingType,
  useGlobalFixedRateOffer,
  useGlobalPriceDiscoveryOffer,
  useGlobalVariableRateOffer,
} from '@/enterprise/data/useGlobalOffer';
import { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import useToggleOffer from '@/enterprise/data/useToggleOffer';
import {
  isDeletableParticipationOffer,
  isOfferValidForUpdate,
  trackParticipationMarketEvents,
} from '@/enterprise/features/globalOffers/utils';
import { useReporting } from '@/reporting';
import useExchangeRates from '@/shared/data/useExchangeRates';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useRestrictions from '@/utils/useRestrictions';
import DeleteOfferConfirmationModal from './DeleteOfferConfirmationModal';
import { getOfferBuilderNavigationState } from './OfferActivityCards';
import useParticipationAction from './useParticipationAction';

const OfferActivityActionsDropdown = ({ buyer }: { buyer: BuyerGroupWithStats }) => {
  const { t } = useTranslation();
  const { getRestrictions } = useRestrictions();
  const { trackEnterpriseEvent } = useReporting();
  const { data: exchangeRates } = useExchangeRates();
  const { executeParticipationAction } = useParticipationAction();
  const [deleteOfferModalOpen, setDeleteOfferModalOpen] = useState(false);
  const { BENCHMARK, FIXED, NAME_YOUR_RATE } = buyer.takerMarketGroups;
  const takerMarketsToEdit = [...BENCHMARK.takerMarkets, ...FIXED.takerMarkets, ...NAME_YOUR_RATE.takerMarkets];
  const { canEditOffers } = getRestrictions(takerMarketsToEdit);
  const { mutateAsync: submitGlobalPriceDiscoveryParticipation } = useGlobalPriceDiscoveryOffer(
    NAME_YOUR_RATE.takerMarkets
  );
  const { mutateAsync: submitGlobalFixedRateParticipation } = useGlobalFixedRateOffer(FIXED.takerMarkets);
  const { mutateAsync: submitGlobalVariableRateParticipation } = useGlobalVariableRateOffer(BENCHMARK.takerMarkets);
  const { mutateAsync: deleteOffer } = useToggleOffer();
  const hasOfferableMarkets = takerMarketsToEdit.some(isTakerMarketOfferable);
  const canBuildOffer = canEditOffers && hasOfferableMarkets && takerMarketsToEdit.length > 0;
  // Express accept and manually awarded offers are never PATCHed; they are deleted one by one after confirmation
  const deletableTakerMarkets = takerMarketsToEdit.filter(isDeletableParticipationOffer);
  const canTurnOffParticipation =
    canEditOffers &&
    (deletableTakerMarkets.length > 0 ||
      takerMarketsToEdit.some((tm) => tm.offerConfig.isEnabled && isOfferValidForUpdate(tm, false)));
  const navigationState = getOfferBuilderNavigationState(takerMarketsToEdit);

  const onParticipationSubmit = async ({ isEnabled }: { isEnabled: boolean }) => {
    if (!canEditOffers) {
      return;
    }

    const submissionBatchId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const succeededGroups: { type: GlobalOfferMarketPricingType; groupTakerMarkets: typeof takerMarketsToEdit }[] = [];

    const onSuccess = (type: GlobalOfferMarketPricingType, groupTakerMarkets: typeof takerMarketsToEdit) => {
      succeededGroups.push({ type, groupTakerMarkets });
      trackParticipationMarketEvents({
        takerMarkets: groupTakerMarkets,
        pricingType: type,
        isEnabled,
        exchangeRates,
        submissionBatchId,
        trackEnterpriseEvent,
      });
    };

    await executeParticipationAction(
      async () => {
        const promises = [];
        // Reason: skip a pricing group with nothing to PATCH so its success tracking does not fire for a no-op
        const hasUpdatableMarkets = (groupTakerMarkets: typeof takerMarketsToEdit) =>
          groupTakerMarkets.some((tm) => isOfferValidForUpdate(tm, isEnabled));

        if (hasUpdatableMarkets(NAME_YOUR_RATE.takerMarkets)) {
          promises.push(
            submitGlobalPriceDiscoveryParticipation(
              { isEnabled },
              { onSuccess: () => onSuccess('NAME_YOUR_RATE', NAME_YOUR_RATE.takerMarkets) }
            )
          );
        }

        if (hasUpdatableMarkets(FIXED.takerMarkets)) {
          promises.push(
            submitGlobalFixedRateParticipation(
              { isEnabled },
              { onSuccess: () => onSuccess('FIXED', FIXED.takerMarkets) }
            )
          );
        }

        if (hasUpdatableMarkets(BENCHMARK.takerMarkets)) {
          promises.push(
            submitGlobalVariableRateParticipation(
              { isEnabled },
              { onSuccess: () => onSuccess('BENCHMARK', BENCHMARK.takerMarkets) }
            )
          );
        }

        if (!isEnabled) {
          deletableTakerMarkets.forEach((tm) => {
            const submitValues = buildTurnOffParticipationParams(tm);
            // Reason: track after the awaited promise, mutate-level callbacks only fire for the latest of concurrent calls
            promises.push(
              deleteOffer(submitValues).then(() =>
                trackEnterpriseEvent(
                  getParticipationOffEventType(tm),
                  buildParticipationOffEventPayload({ takerMarket: tm, submitValues, exchangeRates })
                )
              )
            );
          });
        }

        await Promise.all(promises);

        if (succeededGroups.length > 0) {
          trackEnterpriseEvent('global-participation::submitted', {
            isEnabled,
            submissionBatchId,
            NAME_YOUR_RATE: succeededGroups.find((g) => g.type === 'NAME_YOUR_RATE')?.groupTakerMarkets.length ?? 0,
            BENCHMARK: succeededGroups.find((g) => g.type === 'BENCHMARK')?.groupTakerMarkets.length ?? 0,
            FIXED: succeededGroups.find((g) => g.type === 'FIXED')?.groupTakerMarkets.length ?? 0,
          });
        }
      },
      {
        messageId: buyer.makerOrganizationUuid,
        errorTitle: 'Enterprise Buyer Participation Off Error',
        errorLocation: 'OfferActivity -> OfferActivityActionsDropdown -> onParticipationSubmit',
        errorContext: {
          takerMarkets: takerMarketsToEdit.map((tm) => ({
            makerOrganizationUuid: tm.makerOrganizationUuid,
            marketUuid: tm.marketUuid,
            supplierDivisionUuid: tm.takerDivisionId,
          })),
        },
      }
    );
  };

  const handleTurnOffParticipation = () => {
    if (deletableTakerMarkets.length > 0) {
      setDeleteOfferModalOpen(true);
      return;
    }

    onParticipationSubmit({ isEnabled: false });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton
            icon={EllipsisVerticalIcon}
            name={t('buyerControls.customerActions')}
            size="sm"
            variant="ancillary"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" avoidCollisions={false} collisionPadding={24}>
          <DropdownMenuItem asChild disabled={!canBuildOffer}>
            <Link state={navigationState} to={asEnterpriseLink('build-offer')}>
              {t('core.buildOffer')}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!canTurnOffParticipation} onClick={handleTurnOffParticipation}>
            {t('globalActions.turnParticipationOff')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteOfferConfirmationModal
        open={deleteOfferModalOpen}
        onClose={() => setDeleteOfferModalOpen(false)}
        onConfirm={() => onParticipationSubmit({ isEnabled: false })}
      />
    </>
  );
};

export default OfferActivityActionsDropdown;
